import * as React from "react";
import type { MentionListItem } from "../../utils/collaborateMentions";
import Avatar from "../primitives/Avatar";

export type CollaborateMentionListProps = {
  items: MentionListItem[];
  command: (item: MentionListItem) => void;
  heading?: string;
  showHint?: boolean;
};

export type CollaborateMentionListRef = {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
};

const CollaborateMentionList = React.forwardRef<
  CollaborateMentionListRef,
  CollaborateMentionListProps
>(({ items, command, heading = "", showHint = true }, ref) => {
  const [selectedIndex, setSelectedIndex] = React.useState(0);

  React.useEffect(() => {
    setSelectedIndex(0);
  }, [items]);

  const selectItem = React.useCallback(
    (index: number) => {
      const item = items[index];
      if (item) {
        command(item);
      }
    },
    [command, items]
  );

  React.useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (event.key === "ArrowUp") {
        setSelectedIndex((i) => (i + items.length - 1) % items.length);
        return true;
      }
      if (event.key === "ArrowDown") {
        setSelectedIndex((i) => (i + 1) % items.length);
        return true;
      }
      if (event.key === "Enter" || event.key === "Tab") {
        selectItem(selectedIndex);
        return true;
      }
      return false;
    },
  }));

  if (items.length === 0) {
    return (
      <div className="collaborate-mention-popover">
        <div className="collaborate-mention-popover__empty">No people found</div>
      </div>
    );
  }

  return (
    <div className="collaborate-mention-popover" role="listbox" aria-label="Mention suggestions">
      {heading ? <div className="collaborate-mention-popover__heading">{heading}</div> : null}
      {items.map((item, index) => {
        const active = index === selectedIndex;
        return (
          <button
            key={item.id}
            type="button"
            role="option"
            aria-selected={active}
            className={`collaborate-mention-popover__item${active ? " collaborate-mention-popover__item--active" : ""}`}
            onMouseEnter={() => setSelectedIndex(index)}
            onMouseDown={(e) => {
              e.preventDefault();
              selectItem(index);
            }}
          >
            <Avatar name={item.label} size={22} />
            <span className="collaborate-mention-popover__meta">
              <span className="collaborate-mention-popover__name">{item.label}</span>
              <span className="collaborate-mention-popover__email">{item.email ?? ""}</span>
            </span>
            {showHint ? (
              <span className="collaborate-mention-popover__hint" aria-hidden>
                ↵
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
});

CollaborateMentionList.displayName = "CollaborateMentionList";

export default CollaborateMentionList;
